import { gql } from "@apollo/client";
import { TagsIcon } from "lucide-react";

import { CategoryFragment } from "@/__generated__/graphql";
import { FieldEnum } from "@/components/organisms/Field/FieldEnum";
import { EnumColors } from "@/constants/values";
import { ObjectType } from "@/objects/@types";
import { identifierAndTypenameToId, idToIdentifier } from "@/objects/@utils";
import { State } from "@/state";

const object: ObjectType<CategoryFragment> = {
  humanName: "category",
  inputKey: "categoryObj",
  typename: "CategoryObject",
  ReactIcon: TagsIcon,
  titleFieldKey: "name",
  getTitle: (data) => {
    return data?.name || "";
  },
  getEntityId: (data) => {
    return data?.entity?.id ?? null;
  },
  getReferenceValueOptions: (entityIdentifiers) => {
    return {
      query: gql`
        query CategoryList($entityIdentifiers: [String!]) {
          categories(entityIdentifiers: $entityIdentifiers) {
            data {
              ...Category
            }
          }
        }
      `,
      queryOptions: {
        variables: {
          entityIdentifiers: entityIdentifiers,
        },
      },
      filterOptions(options: CategoryFragment[], filter: string) {
        return options.filter((option) => {
          return option.name?.toLowerCase().includes(filter.toLowerCase());
        });
      },
      mutationOptions: {
        onCreateNewOption: () => {
          for (const entityIdentifier of entityIdentifiers) {
            const state = State.getState();
            const entity = state.entities.find(
              (entity) => entity.identifier === entityIdentifier,
            );
            if (entity) {
              State.getState().pushToCommandStack({
                commandDefinitionId: "manage-categories",
                context: { entityIdentifier },
              });
            }
          }
        },
      },
      onRenderOption: (option: CategoryFragment) => {
        return (
          <FieldEnum
            value={option.name || ""}
            isMultiline={false}
            variant="outlined"
            valueOptions={{
              enum: [
                {
                  key: "name",
                  value: option.name || "",
                },
              ],
              getColors(mode) {
                return {
                  [option.name || ""]: {
                    color: EnumColors[option.color]?.[mode]?.color,
                    backgroundColor:
                      EnumColors[option.color]?.[mode]?.backgroundColor,
                    borderColor: EnumColors[option.color]?.[mode]?.borderColor,
                  },
                };
              },
            }}
            style={{ paddingLeft: 0, paddingRight: 0 }}
          />
        );
      },
      style: "enum",
      // Its colour a dot, on the page's ground: a tag is the tinted one.
      variant: "outlined",
    };
  },
  fieldDefinitions: [
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TagsIcon,
      asString: (data) => data?.name || "",
      getValue: (data) => {
        return data?.name;
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      createProps: {
        isRequired: true,
      },
      summaryProps: {
        isVisible: true,
      },
    },
  ],
};

export { object as CategoryObject };
